import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, Stack, Pagination, Box, Chip, FormControl, Select, MenuItem, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox, RadioGroup, Radio, FormLabel, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import SearchInput from '../../../../utils/searchInput';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import {
    getLabSpecimenRequest,
    getLabSpecimenByAccessionRequest, resetGetLabSpecimenByAccession,
    collectLabSpecimenRequest, resetCollectLabSpecimen,
    receiveLabSpecimenRequest, resetReceiveLabSpecimen,
    rejectLabSpecimenRequest, resetRejectLabSpecimen,
    updateStorageLabSpecimenRequest, resetUpdateStorageLabSpecimen,
    disposeLabSpecimenRequest, resetDisposeLabSpecimen,
    bulkReceiveLabSpecimenRequest, resetBulkReceiveLabSpecimen,
} from '../../../../redux/modules/laboratory/labSpecimen/slice';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import requestHandler from '../../../../utils/requestHandler';
import SignaturePad from '../../../../components/SignaturePad';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const statusColors = {
    pending_collection: 'secondary', collected: 'warning', in_transit: 'info', received_in_lab: 'success',
    processing: 'primary', stored: 'default', disposed: 'default', rejected: 'error'
};

const CONDITIONS = ['acceptable', 'hemolyzed', 'lipemic', 'icteric', 'clotted', 'insufficient', 'other'];
const REJECT_REASONS = ['hemolyzed', 'lipemic', 'clotted', 'insufficient volume', 'wrong tube', 'unlabeled', 'expired', 'leaking', 'other'];

let limit = 10;

export default function LabSpecimens() {
    const dispatch = useDispatch();
    let { getRes, loading: specimenLoading, getByAccessionRes, collectRes, receiveRes, rejectRes, updateStorageRes, disposeRes, bulkReceiveRes } = useSelector(state => state.labSpecimen);
    let { currentBranch } = useSelector(state => state.branch);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    // Accession lookup state
    const [accessionQuery, setAccessionQuery] = useState('');
    const [accessionOpen, setAccessionOpen] = useState(false);

    // Collect dialog state
    const [collectOpen, setCollectOpen] = useState(false);
    const [collectSpec, setCollectSpec] = useState(null);
    const [collectionSite, setCollectionSite] = useState('');

    // Collection consent + collector dropdown (Eureka flow flags)
    const features = useSelector(state => state.hospitalConfig?.features || {});
    const requireCollectionConsent = !!features['lis.collectionConsent'];
    const currentUserId = useSelector(state => state.auth?.userData?.id || state.auth?.userData?._id);
    const [consentConfirmed, setConsentConfirmed] = useState(false);
    const [consentSignedBy, setConsentSignedBy] = useState('');
    const [consentSignature, setConsentSignature] = useState(null);
    const [collectors, setCollectors] = useState([]);
    const [collectedById, setCollectedById] = useState('');

    // Bulk selection state
    const [selectedSpecimens, setSelectedSpecimens] = useState([]);

    // Storage dialog state
    const [storageOpen, setStorageOpen] = useState(false);
    const [storageId, setStorageId] = useState(null);
    const [storageLocation, setStorageLocation] = useState('');

    // Dispose dialog state
    const [disposeOpen, setDisposeOpen] = useState(false);
    const [disposeId, setDisposeId] = useState(null);
    const [disposeReason, setDisposeReason] = useState('');

    // Receive dialog state
    const [receiveOpen, setReceiveOpen] = useState(false);
    const [receiveId, setReceiveId] = useState(null);
    const [condition, setCondition] = useState('acceptable');

    // Reject dialog state
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectId, setRejectId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectNotes, setRejectNotes] = useState('');
    const [recollectionRequired, setRecollectionRequired] = useState(true);

    useEffect(() => {
        dispatch(setMainHeading("Specimen Tracking"));
    }, [dispatch]);

    useEffect(() => {
        if (!currentBranch) return;
        dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
    }, [dispatch, currentBranch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (receiveRes) {
            toast(receiveRes?.message);
            if (receiveRes?.success) {
                dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
            }
            dispatch(resetReceiveLabSpecimen());
        }
    }, [receiveRes, dispatch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (rejectRes) {
            toast(rejectRes?.message);
            if (rejectRes?.success) {
                dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
            }
            dispatch(resetRejectLabSpecimen());
        }
    }, [rejectRes, dispatch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (collectRes) {
            toast(collectRes?.message);
            if (collectRes?.success) {
                dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
                setCollectOpen(false);
                setCollectionSite('');
            }
            dispatch(resetCollectLabSpecimen());
        }
    }, [collectRes, dispatch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (bulkReceiveRes) {
            toast(bulkReceiveRes?.message);
            if (bulkReceiveRes?.success) {
                setSelectedSpecimens([]);
                dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
            }
            dispatch(resetBulkReceiveLabSpecimen());
        }
    }, [bulkReceiveRes, dispatch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (updateStorageRes) {
            toast(updateStorageRes?.message);
            if (updateStorageRes?.success) {
                dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
                setStorageOpen(false);
                setStorageLocation('');
            }
            dispatch(resetUpdateStorageLabSpecimen());
        }
    }, [updateStorageRes, dispatch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (disposeRes) {
            toast(disposeRes?.message);
            if (disposeRes?.success) {
                dispatch(getLabSpecimenRequest({ page: currentPage, limit, searchQuery, status: statusFilter }));
                setDisposeOpen(false);
                setDisposeReason('');
            }
            dispatch(resetDisposeLabSpecimen());
        }
    }, [disposeRes, dispatch, currentPage, searchQuery, statusFilter]);

    useEffect(() => {
        if (getByAccessionRes) {
            if (getByAccessionRes?.success) setAccessionOpen(true);
            else toast(getByAccessionRes?.message || "Specimen not found");
        }
    }, [getByAccessionRes]);

    const pageCount = getRes?.dataCount > limit ? Math.ceil(getRes.dataCount / limit) : 0;

    const handleReceive = (id) => {
        setReceiveId(id);
        setCondition('acceptable');
        setReceiveOpen(true);
    };
    const confirmReceive = () => {
        dispatch(receiveLabSpecimenRequest({ id: receiveId, conditionOnReceipt: condition }));
        setReceiveOpen(false);
        setReceiveId(null);
    };

    const handleCollect = async (spec) => {
        setCollectSpec(spec);
        setCollectionSite('');
        setConsentConfirmed(false);
        setConsentSignature(null);
        setConsentSignedBy(spec.patientId ? `${spec.patientId.fName || ''} ${spec.patientId.lName || ''}`.trim() : '');
        setCollectOpen(true);
        // Technologist dropdown — autofills with the logged-in user
        try {
            const authToken = localStorage.getItem("item");
            const res = await requestHandler(
                `${process.env.REACT_APP_SERVER_API}api/laboratory/staff/collectors`,
                { method: "GET", headers: { "Authorization": authToken, "Content-Type": "application/json" } }
            );
            const list = res?.data || [];
            setCollectors(list);
            setCollectedById(list.find(c => c._id === currentUserId)?._id || currentUserId || '');
        } catch {
            setCollectors([]);
            setCollectedById(currentUserId || '');
        }
    };
    const confirmCollect = () => {
        if (requireCollectionConsent && !consentConfirmed && !consentSignature) {
            toast("Patient consent for sample collection is required first");
            return;
        }
        dispatch(collectLabSpecimenRequest({
            labOrderId: collectSpec.labOrderId?._id || collectSpec.labOrderId,
            specimens: [{ specimenId: collectSpec._id, collectionSite: collectionSite || undefined }],
            collectedById: collectedById || undefined,
            consent: (consentConfirmed || consentSignature) ? {
                confirmed: consentConfirmed,
                signedByName: consentSignedBy || undefined,
                signatureImage: consentSignature || undefined,
            } : undefined,
        }));
    };

    const handleAccessionLookup = () => {
        if (!accessionQuery.trim()) { toast("Enter an accession number"); return; }
        dispatch(getLabSpecimenByAccessionRequest(accessionQuery.trim()));
    };

    const handleBulkReceive = () => {
        const eligible = selectedSpecimens.filter(id => {
            const spec = getRes?.data?.find(s => s._id === id);
            return ['collected', 'in_transit'].includes(spec?.status);
        });
        if (eligible.length === 0) { toast("Select collected/in-transit specimens"); return; }
        dispatch(bulkReceiveLabSpecimenRequest({ specimenIds: eligible, conditionOnReceipt: 'acceptable' }));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedSpecimens(getRes?.data?.map(s => s._id) || []);
        } else {
            setSelectedSpecimens([]);
        }
    };

    const handleSelectSpecimen = (id) => {
        setSelectedSpecimens(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleStorage = (spec) => {
        setStorageId(spec._id);
        setStorageLocation(spec.storageLocation || '');
        setStorageOpen(true);
    };
    const confirmStorage = () => {
        if (!storageLocation.trim()) { toast("Enter a storage location"); return; }
        dispatch(updateStorageLabSpecimenRequest({ id: storageId, storageLocation: storageLocation.trim() }));
    };

    const handleDispose = (spec) => {
        setDisposeId(spec._id);
        setDisposeReason('');
        setDisposeOpen(true);
    };
    const confirmDispose = () => {
        if (!disposeReason.trim()) { toast("Reason is required"); return; }
        dispatch(disposeLabSpecimenRequest({ id: disposeId, reason: disposeReason.trim() }));
    };

    const handleReject = (id) => {
        setRejectId(id);
        setRejectReason('');
        setRejectNotes('');
        setRecollectionRequired(true);
        setRejectOpen(true);
    };
    const confirmReject = () => {
        dispatch(rejectLabSpecimenRequest({ id: rejectId, reason: rejectReason, notes: rejectNotes, recollectionRequired }));
        setRejectOpen(false);
        setRejectId(null);
    };

    return (
        <>
            <Stack mb={3} mt={4} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" flexWrap="wrap">
                <SearchInput setSearchQuery={(v) => { setSearchQuery(v); setCurrentPage(1); }} />
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        size="small" placeholder="Accession # lookup" value={accessionQuery}
                        onChange={(e) => setAccessionQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAccessionLookup()}
                        sx={{ minWidth: 160 }}
                    />
                    <IconButton size="small" onClick={handleAccessionLookup} color="primary"><SearchIcon /></IconButton>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} displayEmpty>
                        <MenuItem value="">All Status</MenuItem>
                        {['pending_collection', 'collected', 'in_transit', 'received_in_lab', 'processing', 'stored', 'disposed', 'rejected'].map(s => (
                            <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            {selectedSpecimens.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" fontWeight={600}>{selectedSpecimens.length} selected</Typography>
                    <Button size="small" variant="contained" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={handleBulkReceive} disabled={specimenLoading}>
                        Receive Selected
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setSelectedSpecimens([])}>Clear</Button>
                </Box>
            )}
            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 400, md: 700 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell padding="checkbox">
                                    <Checkbox
                                        sx={{ color: 'white' }}
                                        checked={getRes?.data?.length > 0 && selectedSpecimens.length === getRes.data.length}
                                        indeterminate={selectedSpecimens.length > 0 && selectedSpecimens.length < (getRes?.data?.length || 0)}
                                        onChange={handleSelectAll}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>Accession #</StyledTableCell>
                                <StyledTableCell>Patient</StyledTableCell>
                                <StyledTableCell>Order</StyledTableCell>
                                <StyledTableCell>Specimen Type</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Collected</StyledTableCell>
                                <StyledTableCell align="right">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getRes?.data?.length > 0 ? getRes.data.map(spec => (
                                <StyledTableRow key={spec._id}>
                                    <StyledTableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedSpecimens.includes(spec._id)}
                                            onChange={() => handleSelectSpecimen(spec._id)}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{spec.accessionNumber}</StyledTableCell>
                                    <StyledTableCell>
                                        {spec.patientId ? `${spec.patientId.fName || ''} ${spec.patientId.lName || ''}`.trim() : '-'}
                                        {spec.patientId?.mrn && <Typography variant="caption" display="block" color="text.secondary">{spec.patientId.mrn}</Typography>}
                                    </StyledTableCell>
                                    <StyledTableCell>{spec.labOrderId?.labOrderId || '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        {spec.specimenTypeId?.name || '-'}
                                        {spec.specimenTypeId?.tubeColor && (
                                            <Chip label={spec.specimenTypeId.tubeColor} size="small" sx={{ ml: 1, textTransform: 'capitalize' }} />
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={spec.status?.replace(/_/g, ' ')} size="small" color={statusColors[spec.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                                        {spec.storageLocation && <Typography variant="caption" display="block" color="text.secondary">{spec.storageLocation}</Typography>}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {spec.collectedAt ? new Date(spec.collectedAt).toLocaleString() : '-'}
                                        {spec.collectedBy && <Typography variant="caption" display="block" color="text.secondary">{spec.collectedBy.firstName || ''} {spec.collectedBy.lastName || ''}</Typography>}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {(spec.status === 'pending_collection' || (spec.status === 'rejected' && spec.rejection?.recollectionRequired)) && (
                                                <Button size="small" variant="outlined" color="primary" startIcon={<PlayCircleOutlineIcon />} onClick={() => handleCollect(spec)}>
                                                    Collect
                                                </Button>
                                            )}
                                            {['collected', 'in_transit'].includes(spec.status) && (
                                                <>
                                                    <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => handleReceive(spec._id)}>
                                                        Receive
                                                    </Button>
                                                    <Button size="small" variant="outlined" color="error" startIcon={<CancelOutlinedIcon />} onClick={() => handleReject(spec._id)}>
                                                        Reject
                                                    </Button>
                                                </>
                                            )}
                                            {['received_in_lab', 'processing', 'stored'].includes(spec.status) && (
                                                <>
                                                    <Button size="small" variant="outlined" startIcon={<InventoryIcon />} onClick={() => handleStorage(spec)}>
                                                        Storage
                                                    </Button>
                                                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleDispose(spec)}>
                                                        Dispose
                                                    </Button>
                                                </>
                                            )}
                                        </Stack>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8}><Typography>No specimens found</Typography></StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {pageCount > 0 && <Pagination count={pageCount} page={currentPage} onChange={(e, v) => setCurrentPage(v)} />}

            {/* Receive Dialog */}
            <Dialog open={receiveOpen} onClose={() => setReceiveOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Receive Specimen</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <FormLabel sx={{ mb: 1 }}>Condition on Receipt</FormLabel>
                        <RadioGroup value={condition} onChange={(e) => setCondition(e.target.value)}>
                            {CONDITIONS.map(c => (
                                <FormControlLabel key={c} value={c} control={<Radio size="small" />} label={c.charAt(0).toUpperCase() + c.slice(1)} />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiveOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={confirmReceive} disabled={specimenLoading}>
                        {specimenLoading ? <CircularProgress size={20} /> : 'Receive'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject Specimen</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <FormLabel sx={{ mb: 1 }}>Rejection Reason</FormLabel>
                        <Select value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} displayEmpty size="small">
                            <MenuItem value="" disabled>Select reason</MenuItem>
                            {REJECT_REASONS.map(r => (
                                <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth label="Notes" multiline rows={2} size="small" sx={{ mt: 2 }}
                        value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                    />
                    <FormControlLabel sx={{ mt: 1 }}
                        control={<Checkbox checked={recollectionRequired} onChange={(e) => setRecollectionRequired(e.target.checked)} />}
                        label="Recollection required"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmReject} disabled={!rejectReason || specimenLoading}>
                        {specimenLoading ? <CircularProgress size={20} /> : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Collect Dialog — consent is the first step when lis.collectionConsent is on */}
            <Dialog open={collectOpen} onClose={() => setCollectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Collect Specimen</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Accession: <strong>{collectSpec?.accessionNumber}</strong>
                        {collectSpec?.labOrderId?.labOrderId && <> &nbsp;|&nbsp; Order: <strong>{collectSpec.labOrderId.labOrderId}</strong></>}
                    </Typography>
                    {requireCollectionConsent && (
                        <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: consentConfirmed || consentSignature ? 'success.light' : 'warning.light', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Step 1 — Patient Consent</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                The patient consents to the collection of this sample for the ordered tests.
                            </Typography>
                            <TextField
                                fullWidth size="small" label="Patient / guardian name" sx={{ mb: 1 }}
                                value={consentSignedBy} onChange={(e) => setConsentSignedBy(e.target.value)}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={consentConfirmed} onChange={(e) => setConsentConfirmed(e.target.checked)} />}
                                label="Consent obtained from patient"
                            />
                            <SignaturePad onChange={setConsentSignature} height={100} label="Patient signature (optional if consent box ticked)" />
                        </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{requireCollectionConsent ? 'Step 2 — Collection Details' : 'Collection Details'}</Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
                        <FormLabel sx={{ mb: 0.5 }}>Collected By</FormLabel>
                        <Select
                            value={collectors.some(c => c._id === collectedById) ? collectedById : ''}
                            onChange={(e) => setCollectedById(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>Select technologist / phlebotomist</MenuItem>
                            {collectors.map(c => (
                                <MenuItem key={c._id} value={c._id}>
                                    {(c.name || `${c.firstName || ''} ${c.lastName || ''}`).trim()} ({c.role})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth size="small" label="Collection Site (optional)" sx={{ mt: 1 }}
                        value={collectionSite} onChange={(e) => setCollectionSite(e.target.value)}
                        placeholder="e.g. Right arm, Left hand"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCollectOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained" onClick={confirmCollect}
                        disabled={specimenLoading || (requireCollectionConsent && !consentConfirmed && !consentSignature)}
                    >
                        {specimenLoading ? <CircularProgress size={20} /> : 'Collect'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Storage Dialog */}
            <Dialog open={storageOpen} onClose={() => setStorageOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Update Storage Location</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth size="small" label="Storage Location" sx={{ mt: 1 }}
                        value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)}
                        placeholder="e.g. Fridge A, Shelf 2"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStorageOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmStorage} disabled={specimenLoading || !storageLocation.trim()}>
                        {specimenLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dispose Dialog */}
            <Dialog open={disposeOpen} onClose={() => setDisposeOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Dispose Specimen</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        This action cannot be undone. The specimen will be marked as disposed.
                    </Typography>
                    <TextField
                        fullWidth size="small" label="Reason for Disposal" required sx={{ mt: 1 }}
                        multiline rows={2}
                        value={disposeReason} onChange={(e) => setDisposeReason(e.target.value)}
                        placeholder="e.g. Sample expired, testing complete"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisposeOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmDispose} disabled={specimenLoading || !disposeReason.trim()}>
                        {specimenLoading ? <CircularProgress size={20} /> : 'Dispose'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Accession Lookup Dialog */}
            <Dialog open={accessionOpen} onClose={() => { setAccessionOpen(false); dispatch(resetGetLabSpecimenByAccession()); }} maxWidth="sm" fullWidth>
                <DialogTitle>Specimen Detail</DialogTitle>
                <DialogContent>
                    {getByAccessionRes?.data && (() => {
                        const s = getByAccessionRes.data;
                        return (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2"><strong>Accession #:</strong> {s.accessionNumber}</Typography>
                                <Typography variant="body2"><strong>Patient:</strong> {s.patientId ? `${s.patientId.fName || ''} ${s.patientId.lName || ''}`.trim() : '-'}</Typography>
                                <Typography variant="body2"><strong>MRN:</strong> {s.patientId?.mrn || '-'}</Typography>
                                <Typography variant="body2"><strong>Specimen Type:</strong> {s.specimenTypeId?.name || '-'}</Typography>
                                <Typography variant="body2"><strong>Status:</strong> <Chip label={s.status?.replace(/_/g, ' ')} size="small" color={statusColors[s.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></Typography>
                                <Typography variant="body2"><strong>Collected:</strong> {s.collectedAt ? new Date(s.collectedAt).toLocaleString('en-GB') : '-'}</Typography>
                                <Typography variant="body2"><strong>Received:</strong> {s.receivedAt ? new Date(s.receivedAt).toLocaleString('en-GB') : '-'}</Typography>
                                <Typography variant="body2"><strong>Condition:</strong> {s.conditionOnReceipt || '-'}</Typography>
                                {s.testIds?.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>Tests:</Typography>
                                        {s.testIds.map((t, i) => (
                                            <Chip key={i} label={t.testCode || t.name || t} size="small" sx={{ mr: 0.5, mt: 0.5 }} />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAccessionOpen(false); dispatch(resetGetLabSpecimenByAccession()); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
